import React, { useRef } from 'react';
import { useDrag } from 'react-dnd';
import { Element as ElementType } from '../../types';
import './Element.css';

interface ElementProps {
  element: ElementType;
  fieldId?: string;
  position?: { x: number; y: number };
  isOnField?: boolean;
  onRemove?: () => void;
}

const Element: React.FC<ElementProps> = ({ 
  element, 
  fieldId, 
  position,
  isOnField = false,
  onRemove
}) => {
  const elementRef = useRef<HTMLDivElement>(null);
  const [showDescription, setShowDescription] = React.useState(false);
  
  // Определяем, является ли устройство мобильным
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  
  // В тач-режиме разрешаем перетаскивать только элементы, которые уже на поле
  const canDrag = !isMobile || (isMobile && isOnField);

  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'element',
    item: () => {
      return { ...element, fieldId, isOnField };
    },
    collect: (monitor) => {
      const isDragging = !!monitor.isDragging();
      return { isDragging };
    },
    options: {
      dropEffect: 'move'
    },
    canDrag
  }), [element, fieldId, isOnField, canDrag]);

  // Функция для установки обоих рефов
  const setRefs = (node: HTMLDivElement | null) => {
    drag(node);
    elementRef.current = node;
  };

  // Обработчик клика для отображения описания
  const handleClick = (e: React.MouseEvent) => {
    // Не показывать описание во время перетаскивания
    if (isDragging) return;
    setShowDescription(!showDescription);
  };

  // Обработчик правого клика для удаления с поля
  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault(); // Предотвращаем появление контекстного меню
    
    // На мобильных устройствах не удаляем элемент при долгом нажатии
    if (!isMobile && isOnField && onRemove) {
      onRemove();
    }
  };

  // Добавляем обработчик для предотвращения появления контекстного меню на мобильных
  const handleTouchStart = (e: React.TouchEvent) => {
    if (isMobile && isOnField) {
      // В мобильном режиме нам нужно предотвратить стандартные действия
      // для элементов, которые можно перетаскивать
      e.preventDefault();
    }
  };

  return (
    <div
      ref={setRefs}
      onClick={handleClick}
      onContextMenu={handleContextMenu}
      onTouchStart={handleTouchStart}
      style={{
        opacity: (() => {
          if (isDragging) {
            if (isMobile) return 0.5;
            return isOnField ? 0 : 0.5;
          }
          return 1;
        })(),
        cursor: canDrag ? 'move' : 'default',
        touchAction: canDrag ? 'none' : 'auto',
        position: isOnField ? 'absolute' : 'relative',        
        ...(position && { left: `${position.x}px`, top: `${position.y}px` }),
      }}
      className={`element ${isDragging && !isMobile && !isOnField ? 'dragging' : ''} ${isOnField ? 'on-field' : ''}`}
    >
      <div className="element-content">
        <span className="element-name">{element.name}</span>
        <span className="element-emoji">{element.emoji}</span>
      </div>
      
      {showDescription && element.description && (
        <div className="element-description">
          {element.description}
        </div>
      )}
    </div>
  );
};

export default Element;
